import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEmptyScope, normalizeScope } from '../utils/scopeSelection';

/*
 * FILTER-UX01 (ТЗ v4 §19.1, FE-FIX16): «State кэшируется отдельно для
 * Statistics и Gantt».
 *
 * Экраны «Статистика» и «Диаграмма Ганта» живут на одном маршруте
 * (/statistics?tab=…), поэтому URL для их фильтров общий и одним кэшем они
 * перетирали бы друг друга. Здесь состояние лежит в sessionStorage под
 * отдельным ключом на экран: reload восстанавливает выбор, а вкладки не
 * смешиваются.
 *
 * Хранится не только scope, но и абсолютный интервал: для Ганты синхронизация
 * панелей определена именно интервалом, и без него восстановленный scope
 * показывал бы данные другого периода.
 *
 * localStorage здесь не используется намеренно: фильтр — это состояние сессии,
 * а не долгосрочная настройка пользователя, и он не должен «догонять» его в
 * другой вкладке или через неделю.
 *
 * В ключ входит логин (как в useTabFilterCache): за одной сессией браузера
 * могут работать разные пользователи, и выбор по недоступной другому объекту
 * ветви не должен всплывать у него.
 */

const currentUserKey = () => {
  try {
    return localStorage.getItem('auth_email') || 'anonymous';
  } catch {
    return 'anonymous';
  }
};

const readCache = (storageKey) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCache = (storageKey, value) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    /* приватный режим / переполненное хранилище — фильтр работает и без кэша */
  }
};

export const useScopeCache = (screenKey, { period: initialPeriod = {} } = {}) => {
  const storageKey = `${screenKey}:${currentUserKey()}`;
  const [state, setState] = useState(() => {
    const cached = readCache(`${screenKey}:${currentUserKey()}`);
    return {
      scope: normalizeScope(cached?.scope),
      period: { ...initialPeriod, ...(cached?.period || {}) },
    };
  });

  useEffect(() => { writeCache(storageKey, state); }, [storageKey, state]);

  const setScope = useCallback((next) => {
    setState((prev) => {
      const scope = normalizeScope(typeof next === 'function' ? next(prev.scope) : next);
      return { ...prev, scope };
    });
  }, []);

  const setPeriod = useCallback((patch) => {
    setState((prev) => ({ ...prev, period: { ...prev.period, ...patch } }));
  }, []);

  /** «Сбросить фильтры» — пустой scope, то есть весь ACL-доступный объём. */
  const resetScope = useCallback(() => {
    setState((prev) => ({ ...prev, scope: createEmptyScope() }));
  }, []);

  return useMemo(
    () => ({ scope: state.scope, period: state.period, setScope, setPeriod, resetScope }),
    [state.scope, state.period, setScope, setPeriod, resetScope]
  );
};

export default useScopeCache;
